import { DTB } from '../_types/dtb';

interface EntityData {
  id: string;
  label: string;
  description: string;
  value?: string;
}

interface TextEntity {
  key: string;
  data: EntityData;
  isCustom?: boolean;
}

/**
 * Extracts unique text entities from GIQ questions
 * Similar to the logic in TextEntityDialog
 */
export function extractUniqueTextEntitiesFromGIQ(giqQuestions: any[]): TextEntity[] {
  if (!giqQuestions || giqQuestions.length === 0) {
    return [];
  }

  const uniqueTextEntities = new Map<string, TextEntity>();

  giqQuestions.forEach((question: any) => {
    if (question.textEntity && question.textEntity.textEntity) {
      const textEntityKey = question.textEntity.textEntity;
      // Only add if not already present (ensures uniqueness)
      if (!uniqueTextEntities.has(textEntityKey)) {
        uniqueTextEntities.set(textEntityKey, {
          key: textEntityKey,
          data: {
            id: question.textEntity.id,
            label: question.textEntity.textEntity,
            description: question.textEntity.description,
            value: question.answer || '',
          },
        });
      }
    }
  });

  return Array.from(uniqueTextEntities.values());
}

/**
 * Processes custom text entities from the API response
 * Similar to the logic in TextEntityDialog
 */
export function processCustomTextEntities(customTextEntities: any[]): TextEntity[] {
  if (!customTextEntities || customTextEntities.length === 0) {
    return [];
  }

  return customTextEntities.map((entity: any) => ({
    key: entity.textEntity,
    data: {
      id: entity.id,
      label: entity.textEntity,
      description: entity.description,
      value: entity.substitutionValue,
    },
    isCustom: true,
  }));
}

/**
 * Processes content by replacing lexientity elements with their resolved values
 * @param content - The HTML content containing lexientity elements
 * @param textEntities - Array of text entities with their resolved values
 * @returns Processed content with lexientity elements replaced
 */
export function processLexientityContent(content: string, textEntities: TextEntity[]): string {
  if (!content || textEntities.length === 0) {
    return content;
  }

  // Check if there are any lexientity tags in the content
  if (!content.includes('<lexientity>')) {
    return content;
  }

  // Create a map for quick lookup
  const entityMap = new Map<string, string>();
  textEntities.forEach(entity => {
    entityMap.set(entity.data.label, entity.data.value || '');
  });

  // Replace lexientity elements with their resolved values
  // This regex handles both simple format <lexientity>[name]</lexientity> 
  // and format with attributes <lexientity title="..." description="...">[name]</lexientity>
  return content.replace(/<lexientity[^>]*>\[([^\]]+)\]<\/lexientity>/g, (match, entityLabel) => {
    const resolvedValue = entityMap.get(entityLabel.trim());
    return resolvedValue || entityLabel; // Fallback to original label if not found
  });
}

/**
 * Combines GIQ text entities and custom text entities into a single array
 * Uses a Map to handle duplicates and ensure custom entities override GIQ entities with the same key
 * @param giqTextEntities - Text entities from GIQ questions
 * @param customTextEntities - Custom text entities from manual
 * @returns Combined array of unique text entities with custom entities taking precedence
 */
export function combineTextEntities(giqTextEntities: TextEntity[], customTextEntities: TextEntity[]): TextEntity[] {
  const entityMap = new Map<string, TextEntity>();

  // First, add GIQ text entities
  giqTextEntities.forEach((entity) => {
    if (entity.key) {
      entityMap.set(entity.key, entity);
    }
  });

  // Then, add custom text entities (these will override GIQ entities with the same key)
  customTextEntities.forEach((entity) => {
    if (entity.key) {
      entityMap.set(entity.key, entity);
    }
  });

  return Array.from(entityMap.values());
}
